// Client-detail voor counselor. PRD-04 §3.2.

import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { CounselorLayout } from "@/components/CounselorLayout";

interface ClientDetail {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  address: { street: string; houseNumber: string; postalCode: string; city: string } | null;
  languagePreference: string;
  lastLoginAt: string | null;
  accountStatus: "ACTIVE" | "LOCKED" | "DEACTIVATED" | "UNKNOWN";
  onboarding: {
    status: string;
    currentStep: number;
    completedAt: string | null;
    analysisPeriodYears: number;
    personalDataComplete: boolean;
    emailConnected: boolean;
    bankAccountsComplete: boolean;
  } | null;
  emailAccountCount: number;
  recentEmails: Array<{
    id: string;
    subject: string | null;
    fromAddress: string;
    receivedAt: string;
    emailType: string | null;
    threatLevel: string | null;
    extractedAmount: string | null;
    extractedCreditor: string | null;
  }>;
  createdAt: string;
}

const THREAT_BADGE: Record<string, string> = {
  CRITICAL: "bg-red-50 text-red-800",
  HIGH: "bg-orange-50 text-orange-800",
  MEDIUM: "bg-amber-50 text-amber-800",
  LOW: "bg-blue-50 text-blue-800",
  NONE: "bg-neutral-100 text-neutral-600",
};

export function ClientDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const query = useQuery({
    queryKey: ["counselor-client", id],
    queryFn: () => api.get<ClientDetail>(`/v1/counselor/clients/${id}`),
    enabled: !!id,
  });

  return (
    <CounselorLayout title={query.data ? `${query.data.firstName} ${query.data.lastName}` : "…"}>
      <Link
        to="/counselor"
        className="inline-block mb-4 text-sm text-neutral-600 hover:text-neutral-900"
      >
        ← {t("back", "Terug")}
      </Link>

      {query.isLoading ? (
        <p className="text-sm text-neutral-500">{t("loading", "Laden…")}</p>
      ) : query.isError ? (
        <p className="text-sm text-red-700">{t("errors.resource.notFound", "Niet gevonden")}</p>
      ) : query.data ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sidebar info */}
          <aside className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3 text-sm">
            <h3 className="font-semibold text-neutral-900">{t("counselor.client.profile", "Profiel")}</h3>
            <Field label={t("counselor.client.email", "E-mail")} value={query.data.email ?? "–"} />
            <Field label={t("counselor.client.phone", "Telefoon")} value={query.data.phone ?? "–"} />
            {query.data.address && (
              <Field
                label={t("counselor.client.address", "Adres")}
                value={`${query.data.address.street} ${query.data.address.houseNumber}, ${query.data.address.postalCode} ${query.data.address.city}`}
              />
            )}
            <Field label={t("counselor.client.language", "Taal")} value={query.data.languagePreference} />
            <Field
              label={t("counselor.client.account", "Account")}
              value={query.data.accountStatus}
              valueClass={
                query.data.accountStatus === "ACTIVE"
                  ? "text-green-700"
                  : query.data.accountStatus === "LOCKED"
                    ? "text-amber-700"
                    : "text-red-700"
              }
            />
            <Field
              label={t("counselor.client.lastLogin", "Laatste login")}
              value={
                query.data.lastLoginAt
                  ? new Date(query.data.lastLoginAt).toLocaleString("nl-NL", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })
                  : "–"
              }
            />
            <Field
              label={t("counselor.client.registered", "Geregistreerd")}
              value={new Date(query.data.createdAt).toLocaleDateString("nl-NL")}
            />
          </aside>

          <div className="lg:col-span-2 space-y-6">
            {/* Onboarding status */}
            <section className="bg-white border border-neutral-200 rounded-xl p-5">
              <h3 className="font-semibold mb-3">
                {t("counselor.client.onboarding", "Onboarding")}
              </h3>
              {query.data.onboarding ? (
                <div className="text-sm space-y-1">
                  <p>
                    <strong>{t("counselor.client.status", "Status")}:</strong>{" "}
                    <span
                      className={
                        query.data.onboarding.status === "COMPLETED"
                          ? "text-green-700"
                          : "text-amber-700"
                      }
                    >
                      {query.data.onboarding.status}
                    </span>{" "}
                    ({t("counselor.client.step", "stap")} {query.data.onboarding.currentStep}/9)
                  </p>
                  <p>
                    {t("counselor.client.period", "Analyseperiode")}: {query.data.onboarding.analysisPeriodYears}{" "}
                    {t("onboarding.welcome.yearSuffix", "jaar")}
                  </p>
                  <ul className="mt-2 text-xs text-neutral-600 space-y-0.5">
                    <li>{query.data.onboarding.personalDataComplete ? "✓" : "○"} Persoonlijke gegevens</li>
                    <li>{query.data.onboarding.emailConnected ? "✓" : "○"} E-mail gekoppeld ({query.data.emailAccountCount} accounts)</li>
                    <li>{query.data.onboarding.bankAccountsComplete ? "✓" : "○"} Bankrekeningen</li>
                  </ul>
                </div>
              ) : (
                <p className="text-sm text-neutral-500">
                  {t("counselor.client.noOnboarding", "Nog niet gestart.")}
                </p>
              )}
            </section>

            {/* Recent emails */}
            <section className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
              <header className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
                <h3 className="font-semibold">
                  {t("counselor.client.recentEmails", "Recente e-mails")}
                </h3>
                <span className="text-xs text-neutral-500">
                  {query.data.recentEmails.length}
                </span>
              </header>
              {query.data.recentEmails.length === 0 ? (
                <p className="p-5 text-sm text-neutral-500">
                  {t("counselor.client.noEmails", "Geen verwerkte e-mails.")}
                </p>
              ) : (
                <ul className="divide-y divide-neutral-100">
                  {query.data.recentEmails.map((e) => (
                    <li key={e.id} className="px-5 py-3 text-sm">
                      <div className="flex items-center gap-2 mb-0.5">
                        {e.threatLevel && (
                          <span
                            className={`text-xs px-2 py-0.5 rounded ${THREAT_BADGE[e.threatLevel]}`}
                          >
                            {e.threatLevel}
                          </span>
                        )}
                        {e.emailType && (
                          <span className="text-xs text-neutral-500 font-mono">{e.emailType}</span>
                        )}
                        <span className="text-xs text-neutral-400 ml-auto">
                          {new Date(e.receivedAt).toLocaleDateString("nl-NL", { dateStyle: "short" })}
                        </span>
                      </div>
                      <p className="font-medium truncate">{e.subject ?? "(geen onderwerp)"}</p>
                      <p className="text-xs text-neutral-600 truncate">{e.fromAddress}</p>
                      {(e.extractedCreditor || e.extractedAmount) && (
                        <p className="text-xs text-neutral-700 mt-0.5">
                          {e.extractedCreditor && <strong>{e.extractedCreditor}</strong>}
                          {e.extractedAmount && ` · € ${e.extractedAmount}`}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </div>
      ) : null}
    </CounselorLayout>
  );
}

function Field({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div>
      <p className="text-xs text-neutral-500">{label}</p>
      <p className={`font-medium ${valueClass ?? "text-neutral-900"}`}>{value}</p>
    </div>
  );
}
